/* eslint-disable react-hooks/exhaustive-deps */
import {FC, ReactElement, useEffect, useState} from "react";
import {TreeItem2, SimpleTreeView, treeItemClasses} from "@mui/x-tree-view";
import {useInterstateToken} from "@interstate/components/InterstateThemeProvider";
import {ChevronDownIcon, ChevronRightIcon} from "@interstate/components/Icons";
import {Spinner} from "@interstate/components/Spinner";
import {TabColumn} from "../../../tab-content";
import {useOperationalEntity} from "../../context";
import {useGraphqlLazyQuery} from "../../../amplify/hooks";
import {lookupOperationalHierarchy as lookupOperationalHierarchyQuery} from "../../../amplify/graphql";
import {
  LookupOperationalHierarchy,
  LookupOperationalHierarchyData,
  OperationalHierarchyNode,
  LookupOperationalHierarchyVariables
} from "../../backend";
import {BackendError} from "@common-core/runtime-js/backend";
import {Endpoints} from "../../../runtime";
import {useToastErrorHandler} from "../../../backend";
import {collectSelectionPath, HierarchyNodeLabel} from "../../hierarchy";
import "./OperationalHierarchyColumn.scss";
import {useFlags} from "launchdarkly-react-client-sdk";
import {features} from "../../../features";

export const OperationalHierarchyColumn: FC = () => {
  const toastError = useToastErrorHandler();
  const token = useInterstateToken();
  const [expanded, setExpanded] = useState<string[]>(() => []);
  const [root, setRoot] = useState<OperationalHierarchyNode>();
  const {
    entity: {id}
  } = useOperationalEntity();
  const flags = useFlags();
  const useSpringBffPassthrough =
    flags[features.dev.springBffPassthrough.searchAndHierarchy];

  const [lookupRoot] = useGraphqlLazyQuery<
    LookupOperationalHierarchyData,
    LookupOperationalHierarchyVariables
  >({
    document: LookupOperationalHierarchy,
    amplifyQuery: lookupOperationalHierarchyQuery,
    fetchPolicy: "no-cache",
    context: {
      endpoint: useSpringBffPassthrough
        ? Endpoints.APPSYNC
        : Endpoints.SPRING_BFF
    }
  });

  const renderTree = (node: OperationalHierarchyNode): ReactElement => {
    const selected = node.id === id;
    const children = node.children ? [...node.children] : [];
    return (
      <TreeItem2
        key={node.id}
        itemId={node.id}
        data-testid={`hierarchy-node-${node.id}`}
        className={`hierarchy-node${selected ? " hierarchy-node--selected" : ""}`}
        label={<HierarchyNodeLabel node={node} />}>
        {children
          ?.sort((a, b) => {
            if (expanded.includes(a.id)) {
              return -1;
            }
            if (expanded.includes(b.id)) {
              return 1;
            }
            return a.organizationInformation.dbaName.localeCompare(
              b.organizationInformation.dbaName
            );
          })
          .map(child => renderTree(child))}
      </TreeItem2>
    );
  };

  useEffect(() => {
    lookupRoot({
      variables: {id}
    })
      .then(result => {
        if (!result.data?.hierarchyNode) {
          throw new BackendError(
            `Root hierarchy node of entity ${id} not found`,
            404
          );
        }
        return result.data.hierarchyNode;
      })
      .then(root => {
        const expanded: string[] = [];
        if (collectSelectionPath(root, id, expanded)) {
          setExpanded(expanded);
          setRoot(root);
        }
      })
      .catch(toastError);
  }, []);

  if (root === undefined || expanded.length === 0) {
    return (
      <TabColumn name={"hierarchy-column"}>
        <Spinner />
      </TabColumn>
    );
  }

  return (
    <TabColumn name={"hierarchy-column"}>
      <SimpleTreeView
        id={`hierarchy-tree-view-${root?.id}`}
        data-testid={`hierarchy-tree-view-${root?.id}`}
        className={"hierarchy-tree-view"}
        sx={{
          [`& .${treeItemClasses.groupTransition}`]: {
            marginLeft: 1.9,
            borderLeft: theme => `1px solid ${theme.palette.divider}`
          }
        }}
        slots={{
          expandIcon: ChevronRightIcon,
          collapseIcon: ChevronDownIcon
        }}
        slotProps={{
          collapseIcon: {
            color: token("sem.color.on-surface.primary")
          },
          expandIcon: {
            color: token("sem.color.on-surface.primary")
          }
        }}
        disableSelection={true}
        disabledItemsFocusable={true}
        defaultExpandedItems={expanded}
        selectedItems={id}>
        {renderTree(root)}
      </SimpleTreeView>
    </TabColumn>
  );
};
